"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";

export interface MentionableItem {
  id: string;
  label: string;
  /** Used for matching when user types after @ */
  searchLabel?: string;
}

interface AtMentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  items: MentionableItem[];
  className?: string;
  /** Use textarea for multiline (e.g. longer messages) */
  multiline?: boolean;
}

/** Extract all @DocumentName references from text. Returns unique labels. */
export function extractMentionedLabels(text: string): string[] {
  const re = /@([^\s@]+)/g;
  const seen = new Set<string>();
  let m;
  while ((m = re.exec(text)) !== null) {
    seen.add(m[1].trim());
  }
  return Array.from(seen);
}

/** Parse @query from the end of text. Returns { query, startIndex } or null. */
function getMentionQuery(text: string): { query: string; startIndex: number } | null {
  const atIdx = text.lastIndexOf("@");
  if (atIdx === -1) return null;
  const afterAt = text.slice(atIdx + 1);
  if (/\s/.test(afterAt)) return null; // space after @ cancels
  return { query: afterAt.toLowerCase(), startIndex: atIdx };
}

export function AtMentionInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  disabled,
  items,
  className,
  multiline = false,
}: AtMentionInputProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const mention = getMentionQuery(value);
  const filteredItems = mention
    ? items.filter(
        (item) =>
          (item.searchLabel ?? item.label)
            .toLowerCase()
            .includes(mention.query)
      )
    : [];
  const showPopover = open && mention !== null && items.length > 0;
  const displayItems = filteredItems.slice(0, 8);

  useEffect(() => {
    if (mention && items.length > 0) {
      setOpen(true);
      setHighlightIndex(0);
    } else {
      setOpen(false);
    }
  }, [mention, items.length]);

  useEffect(() => {
    if (showPopover && displayItems.length > 0) {
      setHighlightIndex((i) => Math.min(i, displayItems.length - 1));
    }
  }, [showPopover, displayItems.length]);

  const insertMention = useCallback(
    (item: MentionableItem) => {
      if (!mention) return;
      const replaceEnd = mention.startIndex + 1 + mention.query.length;
      const before = value.slice(0, mention.startIndex);
      const after = value.slice(replaceEnd);
      const replacement = `@${item.label} `;
      onChange(before + replacement + after);
      setOpen(false);
      inputRef.current?.focus();
      setTimeout(() => {
        const len = (before + replacement).length;
        if (inputRef.current && "setSelectionRange" in inputRef.current) {
          (inputRef.current as HTMLInputElement).setSelectionRange(len, len);
        }
      }, 0);
    },
    [mention, value, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showPopover && displayItems.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, displayItems.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(displayItems[highlightIndex]);
        return;
      }
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
    }
    onKeyDown?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const v = e.target.value;
    onChange(v);
    if (v.endsWith("@") || (mention && !v.slice(mention.startIndex).includes(" "))) {
      setOpen(true);
    }
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <Popover open={showPopover} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative flex-1 min-w-0">
          <InputComponent
            ref={inputRef as any}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={cn("bg-background", className)}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] max-w-[320px] p-0"
        align="start"
        side="top"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-[200px] overflow-auto py-1">
          <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Documents</p>
          {displayItems.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No documents match</p>
          ) : (
            displayItems.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => insertMention(item)}
                onMouseEnter={() => setHighlightIndex(i)}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                  i === highlightIndex && "bg-accent text-accent-foreground"
                )}
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{item.label}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

